import { Toaster } from "@npco/zeller-design-system";
import type { Identity } from "pb.types";
import { useCallback, useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import {
	POSConnectorClient,
	WS_CLOSE_EVENT_CODE_1000_NORMAL_CLOSURE,
	type Payload,
	type POSConnectorMessage,
} from "../../../@lib-pos-connector-client";
import { logger } from "../../../shared/logger";
import { never } from "../../../shared/never";
import type { Unsafe } from "../../../shared/unsafe";
import * as transport from "../../../transport";
import { DeclineResponse_fromPurchasePosConnectorResponse } from "../shared/declined-response";
import { getPageProps } from "../shared/get-declined-page-props";
import type { Route } from "../shared/route";
import { useApiClient } from "../shared/use-api-client";
import { useHealthcheck } from "../shared/use-healthcheck";
import { getApprovedResponse } from "./purchase-with-terminal/get-approved-response";
import { Page } from "./purchase-with-terminal/page";
import { render } from "./purchase-with-terminal/render";
import {
	PurchaseTransactionEventSchema,
	PurchaseAcknowledgedSchema,
	PurchaseResponseSchema,
	PurchaseTerminalBusySchema,
	PurchaseSignatureVerificationSchema,
	type PurchaseResponse,
} from "./schemas";

const resolve = transport.Resolve<"Terminal_purchase">();

export namespace PurchaseWithTerminal {
	export type Props = {
		request: Route.Request<"Terminal_purchase">;
	};
}

export function PurchaseWithTerminal(props: PurchaseWithTerminal.Props) {
	const apiClient = useApiClient();
	const healthcheck = useHealthcheck();
	const toaster = Toaster.useToaster();

	const { request } = props;
	const [sessionId] = useState(() => request.query.sessionUuid ?? uuidv4());

	const [page, _setPage] = useState<Page>(() => Page.Initialising());
	const setPage = useCallback(
		(nextOrFn: Page | ((prev: Page) => Page)) => {
			if (!request.interactive) {
				return undefined;
			}
			return void _setPage((prev) => {
				const next = typeof nextOrFn === "function" ? nextOrFn(prev) : nextOrFn;
				logger.debug("purchase: navigation", {
					from: prev.$type,
					to: next.$type,
				});
				return next;
			});
		},
		[request.interactive],
	);

	useEffect(() => {
		transport.onCancel(() => {
			logger.info("purchase: external cancel called");

			return void done(
				Page.Cancelled({ onClose: closeWithCancelled }),
				"onClose",
				{ skip: true },
			);
		});

		let _timeout: ReturnType<typeof setTimeout> | undefined = undefined;
		let _connection: POSConnectorClient | undefined = undefined;

		function dispose() {
			toaster.clear();
			transport.onCancel(undefined);
			if (_timeout) _timeout = void clearTimeout(_timeout);
			if (_connection) _connection = void _connection.close();
		}

		function updateTimeout(callback: () => void, delay: number) {
			clearTimeout(_timeout);
			_timeout = setTimeout(callback, delay);
		}

		function done<TPage extends Page>(
			page: TPage,
			onNonInteractive: keyof TPage,
			options?: { skip?: boolean },
		): void {
			// Any "done" call should close and cleanup everything all the time.
			dispose();

			if (done.isLocked) {
				return;
			} else {
				done.isLocked = true;
			}

			// Default to `skip: true` if `skip` undefined and request is not interactive.
			const skip = options?.skip ?? !request.interactive;

			return void (skip
				? (page[onNonInteractive] as Unsafe<() => void>)()
				: setPage(page));
		}
		done.isLocked = false;

		const _deviceId = request.headers.deviceId;
		if (!_deviceId) {
			return void (() => {
				logger.error("purchase: empty deviceId request header");

				return void done(
					Page.ErrorOperation({
						onClose: () =>
							void resolve.Error("Setup Required", {
								message: "Not configured.",
							}),
					}),
					"onClose",
				);
			})();
		}

		const deviceId = _deviceId;

		main();
		async function main(isRetry = false) {
			const onRetry = () => void main(true);

			if (isRetry) {
				logger.warn("purchase: retrying");
			}

			done.isLocked = false;
			setPage(Page.Initialising());

			// Prevent continuation if token resolve _after_ timeout.
			if (done.isLocked) {
				return;
			}

			/**
			 * `0` is the initial attampt and used when resetting between reconnection attempts.
			 * `5` is last/final attempt.
			 */
			let _attempt = 0;
			let _hasRequested = false;

			connect();
			async function connect() {
				logger.debug("purchase: connect", {
					attempt: _attempt,
				});

				updateTimeout(() => {
					return void done(
						Page.ErrorOperation({
							onClose: closeWithNetworkFailure,
							onRetry,
						}),
						"onClose",
					);
				}, STATE_INITIAL_TIMEOUT);

				const authToken = (await request.tokens())?.authAccessToken;

				// Prevent continuation if token resolve _after_ timeout.
				if (done.isLocked) {
					return;
				}

				if (!authToken) {
					return void (() => {
						logger.warn("purchase: not authenticated");
						return void done(
							Page.ErrorOperation({
								onClose: () =>
									void resolve.Error("Setup Required", {
										message: "Not authenticated.",
									}),
							}),
							"onClose",
						);
					})();
				}

				transport.onCancel(() => {
					logger.info("purchase: external cancel called");

					if (!connection.ready) {
						return void done(
							Page.Cancelled({ onClose: closeWithCancelled }),
							"onClose",
							{ skip: true },
						);
					}

					return void cancel(true);
				});

				const connection = POSConnectorClient({
					authToken,
					deviceUuid: deviceId,
					onOpen,
					onClose,
					onError,
				});
				_connection = connection;

				/**
				 * Timeout for showing reconnected notification to prevent flickering
				 * if continually failing to open stable connection.
				 */
				let _timeoutReconnected: ReturnType<typeof setTimeout> | undefined =
					undefined;

				function onOpen() {
					logger.debug("purchase: on open");

					if (!_hasRequested) {
						begin();
					} else {
						logger.warn("purchase: on open", {
							attempt: _attempt,
						});

						listen();

						if (!SUPPRESS_STATUS_NOTIFICATIONS) {
							_timeoutReconnected =
								(void clearTimeout(_timeoutReconnected),
								setTimeout(() => {
									toaster.toast({
										message: "Reconnected!",
										status: "positive",
										timeout: "fast",
									});
								}, 1000));
						}
					}
				}

				function reconnect() {
					if (!SUPPRESS_STATUS_NOTIFICATIONS) {
						clearTimeout(_timeoutReconnected);
						toaster.await({ message: "Reconnecting…" });
					}

					logger.warn("purchase: pos connector client reconnecting", {
						attempt: _attempt,
					});

					if (_attempt >= 5) {
						if (!SUPPRESS_STATUS_NOTIFICATIONS) {
							toaster.toast({
								message: "Reconnection failed.",
								status: "negative",
							});
						}
						logger.warn(
							"purchase: pos connector closing with error disconnected",
						);
						return void done(
							Page.ErrorDisconnected({
								onClose: closeWithConnectionFailure,
								onRetry,
							}),
							"onClose",
						);
					}

					updateTimeout(async () => {
						// checks api resolution; retries with refreshed token on 401.
						// success permits web socket reconnect, failure signals network
						// error.
						const result = await healthcheck(deviceId);

						if (result instanceof Error) {
							return void done(
								Page.ErrorNetwork({
									onClose: closeWithNetworkFailure,
									onRetry,
								}),
								"onClose",
							);
						}

						_attempt += 1;
						connect();
					}, 1000 * _attempt);
				}

				function onClose(event: CloseEvent) {
					logger.debug("purchase: on close", {
						code: event.code,
						event: event,
					});

					if (event.code === WS_CLOSE_EVENT_CODE_1000_NORMAL_CLOSURE) {
						return;
					}

					logger.error("purchase: pos connector client on close", {
						eventCode: event.code,
						eventReason: event.reason,
						eventWasClean: event.wasClean,
						connectionReady: connection.ready,
					});

					return void reconnect();
				}

				async function onError(event: Event) {
					logger.error("purchase: pos connector client on error", {
						event: event,
						connectionReady: connection.ready,
					});

					return void reconnect();
				}

				function begin() {
					const payload = PurchaseRequest();

					logger.debug("purchase: request", {
						eventId: payload.eventId,
						transactionId: payload.data.transactionUuid,
					});

					updateTimeout(() => {
						connection.send(CancelRequest());
						return void done(
							Page.ErrorInactivityTimeout({
								onClose: closeWithInactivityTimeout,
								onRetry,
							}),
							"onClose",
						);
					}, STATE_INITIAL_TIMEOUT);

					connection.onMessage(
						handle((message) => {
							{
								// This message is deprecated in favour of
								// `PurchaseResponseSchema`'s `data.responseCode` of
								// "Q9".
								const event =
									PurchaseTerminalBusySchema.safeParse(message).data;
								if (event) {
									return void (() => {
										logger.debug("purchase: terminal busy");
										return void done(
											Page.ErrorTerminalBusy({
												onClose: closeWithPairedDeviceBusy,
												onRetry,
											}),
											"onClose",
										);
									})();
								}
							}
							{
								const event =
									PurchaseAcknowledgedSchema.safeParse(message).data;
								if (event) {
									setPage(Page.InitialisingPayment());
									return void listen();
								}
							}
						}),
					);

					connection.send(payload);
					_hasRequested = true;
				}

				function listen() {
					inactivity();
					function inactivity() {
						updateTimeout(() => {
							connection.send(CancelRequest());
							// NOTE: While the terminal might trigger a timeout error due to inactivity,
							// this fallback ensures we still display a status to the merchant if the
							// connection is completely lost.
							return void done(
								Page.ErrorInactivityTimeout({
									onClose: closeWithInactivityTimeout,
									onRetry,
								}),
								"onClose",
							);
						}, STATE_TRANSACTION_STATE_TIMEOUT);
					}

					function next(page: Page) {
						setPage(page);
						// Reset reconnection attempts when we successfully receive data.
						_attempt = 0;
						// Reset inactivity timeout on every navigation.
						inactivity();
					}

					connection.onMessage(
						handle((message) => {
							{
								const event = PurchaseResponseSchema.safeParse(message).data;
								if (event) {
									return void (() => {
										connection.send({
											action: "purchase",
											eventId: uuidv4(),
											timestamp: Date.now().toString(),
											data: {
												sessionUuid: sessionId,
												state: "ACKNOWLEDGED",
											},
										});

										logger.debug("purchase: response", {
											status: event.data.status,
										});

										if (event.data.status === "APPROVED") {
											return void approved(event);
										} else {
											return void declined(event);
										}
									})();
								}
							}
							{
								const event =
									PurchaseTransactionEventSchema.safeParse(message).data;
								if (event) {
									return void (() => {
										logger.debug("purchase: transaction event", {
											event: event.data.event,
										});

										resolve.Event(event.data.event, {
											message: event.data.displayMessage,
										});

										const sendTransactionEvent = (
											event: "CANCEL" | "PROCEED" | "SKIP",
										) => {
											connection.send({
												action: "transactionEvent",
												eventId: uuidv4(),
												timestamp: Date.now().toString(),
												data: {
													event,
													sessionUuid: sessionId,
												},
											});
										};

										switch (event.data.event) {
											case "AWAITING_ACCOUNT_SELECTION":
												return next(
													Page.AwaitAccountSelection({
														onClose: cancel,
													}),
												);
											case "AWAITING_APPLICATION_SELECTION":
												return next(
													Page.AwaitApplicationSelection({
														onClose: cancel,
													}),
												);
											case "AWAITING_PAYMENT_METHOD":
												return next(
													Page.AwaitPaymentMethod({
														onClose: cancel,
													}),
												);
											case "AWAITING_PIN_ENTRY":
												return next(
													Page.AwaitPinEntry({
														onClose: cancel,
													}),
												);
											case "AWAITING_TIP_ENTRY":
												return next(
													Page.AwaitTipEntry({
														onClose: cancel,
														onSkip: () => sendTransactionEvent("SKIP"),
													}),
												);
											case "PROCESSING":
												return next(Page.AwaitProcessing());
											case "DISPLAYING_PAYMENT_MESSAGE":
											case "RETRYING_PAYMENT_METHOD":
												return next(
													Page.DisplayPaymentMessage({
														onClose: cancel,
													}),
												);
											case "TIP_PROVIDED":
												return next(
													Page.TipProvided({
														message: event.data.displayMessage,
														onClose: cancel,
														/* v8 ignore next -- render maps TipProvided to an intermediate template with no Next button to trigger this */
														onNext: () => sendTransactionEvent("PROCEED"),
													}),
												);
											/* v8 ignore start -- exhaustiveness guard, unreachable for valid transaction event variants */
											default:
												logger.warn(
													"purchase: transaction event unhandled variant",
													{
														data: event.data,
													},
												);
												return never(event.data.event);
											/* v8 ignore stop */
										}
									})();
								}
							}
							{
								const event =
									PurchaseSignatureVerificationSchema.safeParse(message)?.data;
								if (event) {
									return void (() => {
										logger.warn("purchase: signature verification", {
											state: event.data.state,
										});

										resolve.Event(event.data.state, {
											message: event.data.signature,
										});

										switch (event.data.state) {
											case "AWAITING_SIGNATURE":
											case "SIGNATURE_CLEARED":
												return next(
													Page.AwaitSignature({
														onClose: cancel,
													}),
												);
											case "SIGNATURE_SUBMITTED": {
												const sendSignatureVerificationEvent = (
													isValid: boolean,
												) => {
													connection.send({
														action: "signatureVerification",
														eventId: uuidv4(),
														timestamp: Date.now().toString(),
														data: {
															event: "SIGNATURE_VERIFICATION",
															isValid,
															sessionUuid: sessionId,
														},
													});
												};
												return next(
													Page.SignatureSubmitted({
														signature: event.data.signature,
														onClose: closeWithCancelled,
														onApprove: () =>
															sendSignatureVerificationEvent(true),
														onDecline: () =>
															sendSignatureVerificationEvent(false),
													}),
												);
											}
											/* v8 ignore start -- exhaustiveness guard, unreachable for valid signature states */
											default:
												logger.warn(
													"purchase: signature verification event unhandled variant",
													{
														data: event.data,
													},
												);
												return never(event.data.state);
											/* v8 ignore stop */
										}
									})();
								}
							}
						}),
					);
				}

				function approved(event: PurchaseResponse) {
					const { response, getPageProps } = getApprovedResponse({
						event: event.data,
						reference: request.query.reference,
					});
					return void done(
						Page.Approved({
							...getPageProps(),
							onDone: () => void resolve(response),
						}),
						"onDone",
					);
				}

				function declined(event: PurchaseResponse) {
					const response = DeclineResponse_fromPurchasePosConnectorResponse({
						data: event.data,
						reference: request.query.reference,
					});
					return void done(
						Page.Declined({
							...getPageProps({ data: event.data }),
							onCancel: () => void resolve(response),
							onRetry,
						}),
						"onCancel",
					);
				}

				function cancel(external = false) {
					/* v8 ignore start -- UI close handlers forward the click event, so `external` is always truthy here; the falsy branch is only reachable via a direct zero-arg call */
					if (!external) {
						logger.info("purchase: internal cancel called");
					}
					/* v8 ignore stop */

					setPage((prev) => {
						return { ...prev, cancelling: true };
					});

					connection.onMessage(
						handle((message) => {
							const event = PurchaseResponseSchema.safeParse(message).data;
							if (event) {
								return void (() => {
									logger.debug("purchase: response", {
										status: event.data.status,
									});

									if (event.data.status === "APPROVED") {
										return approved(event);
									} else {
										const response =
											DeclineResponse_fromPurchasePosConnectorResponse({
												data: event.data,
												reference: request.query.reference,
											});
										return void done(
											Page.Cancelled({
												onClose: () => void resolve(response),
											}),
											"onClose",
											{ skip: true },
										);
									}
								})();
							}
						}),
					);

					connection.send(CancelRequest());
					updateTimeout(() => {
						return void done(
							Page.Cancelled({
								onClose: closeWithCancelled,
							}),
							"onClose",
						);
					}, STATE_TRANSACTION_STATE_TIMEOUT);
				}

				function handle(
					callback: (
						message: Identity<Omit<POSConnectorMessage, "error">>,
					) => void,
				) {
					return (message: POSConnectorMessage | Error): void => {
						if (message instanceof Error) {
							return void (() => {
								logger.error(
									"purchase: pos connector client on message error",
									{
										errorName: message.name,
										errorMessage: message.message,
									},
								);
							})();
						}
						if (message.error) {
							if (message.error.type === "PAIRED_DEVICE_UNREACHABLE") {
								return void done(
									Page.ErrorTerminalUnreachable({
										onClose: closeWithConnectionFailure,
										onRetry,
									}),
									"onClose",
								);
							}
							if (message.error.type === "FORBIDDEN") {
								return void done(
									Page.ErrorTerminalMissing({
										onClose: closeWithConnectionFailure,
									}),
									"onClose",
								);
							}
							return void done(
								Page.ErrorOperation({
									onClose: closeWithConnectionFailure,
									onRetry,
								}),
								"onClose",
							);
						}

						return void callback(message);
					};
				}
			}
		}

		function CancelRequest() {
			return {
				action: "transactionEvent",
				eventId: uuidv4(),
				timestamp: Date.now().toString(),
				data: {
					sessionUuid: sessionId,
					event: "CANCEL",
				},
			} satisfies Payload;
		}

		function PurchaseRequest() {
			const now = new Date();
			return {
				action: "purchase",
				eventId: uuidv4(),
				timestamp: now.getTime().toString(),
				data: {
					// Currently required by backend.
					transactionUuid: uuidv4(),
					sessionUuid: sessionId,
					amount: request.query.amount,
					tipAmount: request.query.tipAmount ?? 0,
					externalReference: request.query.reference,
					timestamp: now.toISOString(),
					terminalContact: request.query.terminalContact,
					terminalNote: request.query.terminalNote,
					terminalReceipt: request.query.terminalReceipt,
					terminalReceiptPrint: request.query.terminalReceiptPrint,
				},
			} satisfies Payload;
		}

		return () => void dispose();
	}, [apiClient, healthcheck, toaster, request, sessionId, setPage]);

	return render(page);
}

const STATE_INITIAL_TIMEOUT = 15000; // 15 seconds
const STATE_TRANSACTION_STATE_TIMEOUT = 40000; // 40 seconds

function closeWithCancelled() {
	return void resolve.Error("Cancelled");
}

function closeWithConnectionFailure() {
	return void resolve.Error("Connection Failure");
}

function closeWithInactivityTimeout() {
	return void resolve.Error("Inactivity Timeout");
}

function closeWithNetworkFailure() {
	return void resolve.Error("Network Failure");
}

function closeWithPairedDeviceBusy() {
	return void resolve.Error("Paired Device Busy");
}

/**
 * I don't want to remove the status notifications because I believe
 * that they are critical to a user's experience of being able to self-
 * diagnose and resolve network failures in high-traffic, customer-
 * facing environments. It is not "cleaner" to hide them; it is
 * obstructing the user's ability to resolve a common system fault in a
 * real-world environment with imperfect network conditions.
 *
 * Set to `false` to restore the reconnecting status notifications.
 * Remove flag when the decision to restore them is confirmed.
 */
const SUPPRESS_STATUS_NOTIFICATIONS = true;
